<script setup>
/* eslint-disable no-use-before-define */
import { computed, onMounted, ref, watch } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';
import { useStore } from 'dashboard/composables/store';
import TalkviaCreditDashboardAPI from 'dashboard/api/talkviaCreditDashboard';
import InboxSetupGuide from './InboxSetupGuide.vue';
import NoCorbanDashboard from './NoCorbanDashboard.vue';
import AnnouncementCarousel from './AnnouncementCarousel.vue';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

const { accountScopedRoute } = useAccount();
const store = useStore();
const currentAccount = useMapGetter('getCurrentAccount');
const currentUser = useMapGetter('getCurrentUser');
const currentRole = useMapGetter('getCurrentRole');
const inboxes = useMapGetter('inboxes/getInboxes');
const inboxUiFlags = useMapGetter('inboxes/getUIFlags');

const hasInboxes = computed(() => (inboxes.value || []).length > 0);
const isLoadingInboxes = computed(
  () => inboxUiFlags.value?.isFetching ?? false
);
const corbanFeatureFlags = [
  FEATURE_FLAGS.TALKVIA_CORBAN_FACTA_OFFLINE,
  FEATURE_FLAGS.TALKVIA_CORBAN_CLT_FGTS_QUERY,
  FEATURE_FLAGS.TALKVIA_CORBAN_CREDIT_ENGINE,
  FEATURE_FLAGS.TALKVIA_CORBAN_MANUAL_SIMULATORS,
  FEATURE_FLAGS.TALKVIA_CORBAN_PROPOSAL_PIPELINE,
];

const hasCorbanModule = computed(() => {
  const features = currentAccount.value?.features || {};
  return corbanFeatureFlags.some(feature => Boolean(features[feature]));
});

const shouldShowNoCorbanDashboard = computed(() => !hasCorbanModule.value);
const isLoadingHomeState = computed(
  () => !hasInboxes.value && (isPreparingHome.value || isLoadingInboxes.value)
);

const credit = ref(null);
const isLoading = ref(false);
const isPreparingHome = ref(true);

const totals = computed(() => credit.value?.totals || {});
const funnel = computed(() => credit.value?.funnel || []);
const bankIntegrations = computed(() => credit.value?.bank_integrations || []);
const recentActivity = computed(() => credit.value?.recent_activity || []);

const operatorName = computed(
  () => currentUser.value?.name?.split(' ')[0] || 'time'
);

const accountName = computed(() => currentAccount.value?.name || 'Talkvia');

const profileMode = computed(() => {
  if (currentUser.value?.type === 'SuperAdmin') return 'super_admin';
  if (currentRole.value === 'administrator') return 'admin';
  return 'operator';
});

const profileCopy = computed(
  () =>
    ({
      super_admin: {
        badge: 'Visão da plataforma',
        title: `Bem-vindo, ${operatorName.value}. A operação completa está aqui.`,
        description:
          'Acompanhe crédito, canais, produção e pontos de atenção sem navegar por telas técnicas.',
      },
      admin: {
        badge: 'Gestão comercial',
        title: `Bom trabalho, ${operatorName.value}. Seu time em movimento.`,
        description:
          'Veja produção, gargalos do funil e atalhos para destravar propostas e contratos.',
      },
      operator: {
        badge: 'Mesa de atendimento',
        title: `Olá, ${operatorName.value}. Seus próximos atendimentos começam aqui.`,
        description:
          'Retome conversas, veja propostas recentes e siga a próxima melhor ação do cliente.',
      },
    })[profileMode.value]
);

const primaryMetrics = computed(() => [
  {
    label: 'Pago concluído',
    value: formatCurrency(totals.value.paid_amount),
    hint: `${formatNumber(totals.value.paid_count)} propostas pagas`,
    icon: 'i-lucide-circle-check-big',
    tone: 'grass',
  },
  {
    label: 'Ofertado',
    value: formatCurrency(totals.value.offered_amount),
    hint: `${formatNumber(totals.value.simulations_count)} simulações`,
    icon: 'i-lucide-badge-dollar-sign',
    tone: 'teal',
  },
  {
    label: 'Digitado',
    value: formatCurrency(totals.value.typed_amount),
    hint: `${formatNumber(totals.value.proposals_count)} propostas`,
    icon: 'i-lucide-file-pen-line',
    tone: 'blue',
  },
  {
    label: 'Conversão',
    value: formatPercent(totals.value.lead_to_paid_conversion),
    hint: 'Lead para contrato concluído',
    icon: 'i-lucide-route',
    tone: 'amber',
  },
]);

const quickActions = computed(() => [
  {
    label: 'Abrir atendimentos',
    description: 'Retomar conversas, pendências e novos contatos.',
    icon: 'i-lucide-message-circle',
    to: accountScopedRoute('talkvia_conversations'),
    tone: 'blue',
  },
  ...(profileMode.value === 'operator'
    ? []
    : [
        {
          label: 'Abrir Pulso CLT',
          description: 'Ler conversão, produção e saúde bancária.',
          icon: 'i-lucide-activity',
          to: accountScopedRoute('talkvia_credit_dashboard'),
          tone: 'teal',
        },
      ]),
  {
    label: 'Esteira',
    description: 'Mover propostas até assinatura e pagamento.',
    icon: 'i-lucide-panel-top',
    to: accountScopedRoute('talkvia_proposals'),
    tone: 'slate',
  },
  {
    label: 'Agenda',
    description: 'Conferir retornos, entregas e compromissos.',
    icon: 'i-lucide-calendar-days',
    to: accountScopedRoute('talkvia_calendar'),
    tone: 'amber',
  },
]);

const operationalCards = computed(() => [
  {
    label: 'Bancos online',
    value: formatNumber(bankIntegrations.value.length),
    detail:
      bankIntegrations.value.length > 0
        ? bankIntegrations.value
            .slice(0, 3)
            .map(bank => bank.label)
            .join(', ')
        : 'Nenhuma simulação no período',
    icon: 'i-lucide-landmark',
  },
  {
    label: 'Perdas no período',
    value: formatCurrency(totals.value.lost_amount),
    detail: `${formatNumber(totals.value.lost_count)} registros perdidos`,
    icon: 'i-lucide-trending-down',
  },
  {
    label: 'Produção por lead',
    value: formatCurrency(totals.value.production_per_lead),
    detail: `${formatNumber(totals.value.leads_count)} leads medidos`,
    icon: 'i-lucide-user-round-check',
  },
]);

const strongestFunnelStage = computed(() => {
  if (!funnel.value.length) return null;
  return funnel.value.reduce((best, stage) =>
    Number(stage.conversion || 0) > Number(best.conversion || 0) ? stage : best
  );
});

const nextBestAction = computed(() => {
  const stage = funnel.value.find(item => item.status === 'below');
  if (!stage) {
    return {
      title: 'Operação saudável',
      body: 'O funil está rodando acima da média nas etapas críticas. Mantenha o ritmo e revise os leads recentes.',
      to: accountScopedRoute('talkvia_credit_dashboard'),
    };
  }
  return {
    title: `Atenção em ${stage.label.toLowerCase()}`,
    body: `A conversão está ${Math.abs(stage.delta)}pp abaixo da média. Priorize contatos parados nessa etapa.`,
    to: accountScopedRoute('talkvia_proposals'),
  };
});

const funnelHealth = computed(() => {
  const below = funnel.value.filter(stage => stage.status === 'below').length;
  if (!funnel.value.length) {
    return {
      label: 'Aguardando volume',
      detail: 'O funil aparece assim que a operação gerar dados.',
      tone: 'slate',
      icon: 'i-lucide-loader-circle',
    };
  }
  if (below === 0) {
    return {
      label: 'Funil saudável',
      detail: 'Todas as etapas estão acima da média configurada.',
      tone: 'grass',
      icon: 'i-lucide-shield-check',
    };
  }
  return {
    label: `${below} etapa${below > 1 ? 's' : ''} abaixo da média`,
    detail: 'Priorize propostas paradas e simulações com saldo.',
    tone: 'amber',
    icon: 'i-lucide-triangle-alert',
  };
});

const loadHome = async () => {
  isLoading.value = true;
  try {
    const { data } = await TalkviaCreditDashboardAPI.get({ period: '30d' });
    credit.value = data;
  } catch {
    useAlert('Não foi possível carregar a home operacional.');
  } finally {
    isLoading.value = false;
  }
};

function formatCurrency(value) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
}

function formatNumber(value) {
  return new Intl.NumberFormat('pt-BR').format(Number(value || 0));
}

function formatPercent(value) {
  return `${Number(value || 0).toLocaleString('pt-BR', {
    maximumFractionDigits: 1,
  })}%`;
}

function formatTime(value) {
  if (!value) return '';
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}

function toneClass(tone) {
  return {
    grass: 'bg-n-grass-3 text-n-grass-11',
    teal: 'bg-n-teal-3 text-n-teal-11',
    blue: 'bg-n-blue-3 text-n-blue-11',
    amber: 'bg-n-amber-3 text-n-amber-11',
    slate: 'bg-n-slate-3 text-n-slate-11',
  }[tone];
}

function funnelWidth(stage) {
  return `${Math.min(Number(stage.top_percentage || 0), 100)}%`;
}

watch(
  hasCorbanModule,
  hasModule => {
    if (!hasModule || credit.value || isLoading.value) return;

    loadHome();
  },
  { immediate: true }
);

onMounted(async () => {
  try {
    await store.dispatch('inboxes/get');
  } finally {
    isPreparingHome.value = false;
  }
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div
    v-if="isLoadingHomeState"
    class="flex h-full items-center justify-center"
  >
    <span class="i-lucide-loader-2 size-8 animate-spin text-n-slate-9" />
  </div>
  <InboxSetupGuide v-else-if="!hasInboxes" />
  <NoCorbanDashboard
    v-else-if="shouldShowNoCorbanDashboard"
    :account-name="accountName"
    :operator-name="operatorName"
    :has-corban-module="hasCorbanModule"
  />
  <main v-else class="h-full overflow-y-auto bg-n-background">
    <section class="border-b border-n-weak px-6 py-6">
      <div
        class="flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between"
      >
        <div class="max-w-3xl">
          <div class="mb-3 flex items-center gap-2 text-sm text-n-slate-10">
            <span
              class="grid size-7 place-items-center rounded-lg bg-n-teal-3 text-n-teal-11"
            >
              <span class="i-lucide-sparkles size-4" />
            </span>
            {{ profileCopy.badge }} · {{ accountName }}
          </div>
          <h1 class="mb-2 text-2xl font-semibold text-n-slate-12">
            {{ profileCopy.title }}
          </h1>
          <p class="mb-0 text-sm leading-6 text-n-slate-11">
            {{ profileCopy.description }}
          </p>
        </div>

        <router-link
          :to="accountScopedRoute('talkvia_credit_dashboard')"
          class="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-n-teal-9 px-4 text-sm font-medium text-white hover:bg-n-teal-10"
        >
          <span class="i-lucide-chart-no-axes-combined size-4" />
          Ver dashboard de crédito
        </router-link>
      </div>
    </section>

    <section class="grid gap-5 p-6">
      <AnnouncementCarousel />

      <div
        v-if="isLoading"
        class="flex h-32 items-center justify-center rounded-lg border border-n-weak text-sm text-n-slate-11"
      >
        <span class="i-lucide-loader-2 mr-2 size-4 animate-spin" />
        Preparando sua mesa de operação...
      </div>

      <div v-else class="grid gap-5">
        <div class="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <article
            v-for="metric in primaryMetrics"
            :key="metric.label"
            class="rounded-lg border border-n-weak bg-n-alpha-1 p-4"
          >
            <div class="mb-5 flex items-start justify-between gap-3">
              <span class="text-sm font-medium text-n-slate-10">
                {{ metric.label }}
              </span>
              <span
                class="grid size-9 place-items-center rounded-lg"
                :class="toneClass(metric.tone)"
              >
                <span class="size-5" :class="metric.icon" />
              </span>
            </div>
            <strong class="block text-2xl font-semibold text-n-slate-12">
              {{ metric.value }}
            </strong>
            <p class="mb-0 mt-2 text-sm text-n-slate-11">
              {{ metric.hint }}
            </p>
          </article>
        </div>

        <div
          class="grid gap-5 xl:grid-cols-[minmax(0,1.25fr)_minmax(22rem,0.75fr)]"
        >
          <section class="rounded-lg border border-n-weak bg-n-alpha-1 p-5">
            <div class="mb-5 flex items-start justify-between gap-3">
              <div>
                <h2 class="mb-1 text-base font-semibold text-n-slate-12">
                  Fluxo principal
                </h2>
                <p class="mb-0 text-sm text-n-slate-11">
                  Acesse os pontos que movem a operação.
                </p>
              </div>
              <span
                class="rounded-full bg-n-alpha-2 px-3 py-1 text-xs text-n-slate-11"
              >
                30 dias
              </span>
            </div>

            <div class="grid gap-3 md:grid-cols-2">
              <router-link
                v-for="action in quickActions"
                :key="action.label"
                :to="action.to"
                class="group flex min-h-[7rem] items-start gap-3 rounded-lg border border-n-weak p-4 transition hover:border-n-brand/50 hover:bg-n-alpha-2"
              >
                <span
                  class="grid size-10 shrink-0 place-items-center rounded-lg"
                  :class="toneClass(action.tone)"
                >
                  <span class="size-5" :class="action.icon" />
                </span>
                <span class="min-w-0">
                  <span class="block text-sm font-semibold text-n-slate-12">
                    {{ action.label }}
                  </span>
                  <span class="mt-1 block text-sm leading-5 text-n-slate-11">
                    {{ action.description }}
                  </span>
                </span>
                <span
                  class="i-lucide-arrow-up-right ml-auto size-4 shrink-0 text-n-slate-9 transition group-hover:text-n-slate-12"
                />
              </router-link>
            </div>
          </section>

          <aside class="rounded-lg border border-n-weak bg-n-alpha-1 p-5">
            <div class="mb-4 flex items-center gap-3">
              <span
                class="grid size-10 place-items-center rounded-lg bg-n-amber-3 text-n-amber-11"
              >
                <span class="i-lucide-lightbulb size-5" />
              </span>
              <div>
                <h2 class="mb-0 text-base font-semibold text-n-slate-12">
                  Próxima melhor ação
                </h2>
                <p class="mb-0 text-xs text-n-slate-10">
                  Sugerido pela operação
                </p>
              </div>
            </div>
            <h3 class="mb-2 text-lg font-semibold text-n-slate-12">
              {{ nextBestAction.title }}
            </h3>
            <p class="mb-4 text-sm leading-6 text-n-slate-11">
              {{ nextBestAction.body }}
            </p>
            <router-link
              :to="nextBestAction.to"
              class="inline-flex h-9 items-center gap-2 rounded-lg border border-n-weak px-3 text-sm font-medium text-n-slate-12 hover:bg-n-alpha-2"
            >
              Abrir contexto
              <span class="i-lucide-arrow-right size-4" />
            </router-link>
          </aside>
        </div>

        <div
          class="grid gap-5 xl:grid-cols-[minmax(22rem,0.8fr)_minmax(0,1.2fr)]"
        >
          <section class="rounded-lg border border-n-weak bg-n-alpha-1 p-5">
            <div class="mb-5 flex items-start justify-between gap-3">
              <div>
                <h2 class="mb-1 text-base font-semibold text-n-slate-12">
                  Funil vivo
                </h2>
                <p class="mb-0 text-sm text-n-slate-11">
                  O caminho do lead até o contrato concluído.
                </p>
              </div>
              <span
                class="grid size-10 place-items-center rounded-lg"
                :class="toneClass(funnelHealth.tone)"
              >
                <span class="size-5" :class="funnelHealth.icon" />
              </span>
            </div>
            <div class="mb-5 rounded-lg border border-n-weak px-3 py-2">
              <div class="text-sm font-semibold text-n-slate-12">
                {{ funnelHealth.label }}
              </div>
              <p class="mb-0 mt-1 text-xs leading-5 text-n-slate-11">
                {{ funnelHealth.detail }}
              </p>
            </div>
            <div class="grid gap-4">
              <div v-for="stage in funnel" :key="stage.key">
                <div class="mb-2 flex items-center justify-between gap-3">
                  <span class="truncate text-sm font-medium text-n-slate-12">
                    {{ stage.label }}
                  </span>
                  <span class="text-xs text-n-slate-10">
                    {{ formatPercent(stage.conversion) }}
                  </span>
                </div>
                <div class="h-2 rounded-full bg-n-alpha-2">
                  <div
                    class="h-2 rounded-full bg-n-teal-9"
                    :style="{ width: funnelWidth(stage) }"
                  />
                </div>
              </div>
              <div
                v-if="!funnel.length"
                class="rounded-lg border border-dashed border-n-weak p-5 text-sm text-n-slate-11"
              >
                Ainda não há funil suficiente. Quando as primeiras simulações
                entrarem, a leitura aparece aqui.
              </div>
            </div>
          </section>

          <section class="rounded-lg border border-n-weak bg-n-alpha-1 p-5">
            <div class="mb-4 flex items-center justify-between">
              <div>
                <h2 class="mb-1 text-base font-semibold text-n-slate-12">
                  Pulso da operação
                </h2>
                <p class="mb-0 text-sm text-n-slate-11">
                  Indicadores rápidos e últimas movimentações.
                </p>
              </div>
              <span
                v-if="strongestFunnelStage"
                class="rounded-full bg-n-grass-3 px-3 py-1 text-xs font-medium text-n-grass-11"
              >
                Melhor etapa: {{ strongestFunnelStage.label }}
              </span>
            </div>

            <div class="mb-5 grid gap-3 md:grid-cols-3">
              <div
                v-for="card in operationalCards"
                :key="card.label"
                class="rounded-lg border border-n-weak p-3"
              >
                <span
                  class="mb-3 grid size-8 place-items-center rounded-lg bg-n-alpha-2 text-n-slate-11"
                >
                  <span class="size-4" :class="card.icon" />
                </span>
                <strong class="block text-lg font-semibold text-n-slate-12">
                  {{ card.value }}
                </strong>
                <span class="mt-1 block text-xs text-n-slate-10">
                  {{ card.label }}
                </span>
                <p class="mb-0 mt-2 truncate text-xs text-n-slate-11">
                  {{ card.detail }}
                </p>
              </div>
            </div>

            <div class="grid gap-2">
              <article
                v-for="item in recentActivity.slice(0, 5)"
                :key="`${item.type}-${item.occurred_at}-${item.title}`"
                class="flex items-center gap-3 rounded-lg bg-n-alpha-1 px-3 py-2"
              >
                <span
                  class="grid size-8 place-items-center rounded-lg bg-n-alpha-2 text-n-slate-11"
                >
                  <span class="i-lucide-activity size-4" />
                </span>
                <div class="min-w-0 flex-1">
                  <div class="truncate text-sm font-medium text-n-slate-12">
                    {{ item.title }}
                  </div>
                  <div class="truncate text-xs text-n-slate-10">
                    {{ item.subtitle }} · {{ formatTime(item.occurred_at) }}
                  </div>
                </div>
                <span class="shrink-0 text-sm font-semibold text-n-slate-12">
                  {{ formatCurrency(item.amount) }}
                </span>
              </article>
              <div
                v-if="!recentActivity.length"
                class="rounded-lg border border-dashed border-n-weak p-5 text-sm text-n-slate-11"
              >
                Nenhuma movimentação recente. Assim que houver simulações,
                propostas ou contratos, a linha do tempo aparece aqui.
              </div>
            </div>
          </section>
        </div>
      </div>
    </section>
  </main>
</template>
